// Sign-in screens: login, first-run setup, invite acceptance and password reset.
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Eye, EyeOff, LoaderCircle } from '@/components/ui/icons';
import { authApi, errorMessage, type AuthStatus, type InviteInfo } from '@/lib/auth';
import { AtriumLogo } from '@/components/ui/AtriumLogo';

/** After a successful sign-in, reload so the app bootstraps with the new session. */
function enterApp(path = '/') {
  window.location.assign(path);
}

/** The same-origin path in ?next= (e.g. an AI app's /oauth/authorize request), else '/'. */
function nextPath() {
  const next = new URLSearchParams(window.location.search).get('next');
  return next && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/\\') ? next : '/';
}

function AuthCard({ title, subtitle, children, footer }: { title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="login">
      {/* desktop app: no top bar here, so this strip drags the window (shell.css) */}
      <div className="window-drag" aria-hidden />
      <div className="login-card">
        <AtriumLogo size={40} />
        <h1>{title}</h1>
        {subtitle && (
          <p className="muted" style={{ fontSize: 'var(--fs-md)', marginBottom: 16 }}>
            {subtitle}
          </p>
        )}
        {children}
        {footer && <div className="auth-footer">{footer}</div>}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="form-field">
      <label>{label}</label>
      {children}
    </div>
  );
}

function PasswordInput({ value, onChange, autoFocus, placeholder = 'At least 8 characters', autoComplete = 'new-password' }: { value: string; onChange: (v: string) => void; autoFocus?: boolean; placeholder?: string; autoComplete?: string }) {
  const [show, setShow] = useState(false);
  return (
    <div className="pw-input">
      <input className="input" type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} autoFocus={autoFocus} placeholder={placeholder} autoComplete={autoComplete} />
      <button type="button" className="icon-btn sm" onClick={() => setShow(!show)} aria-label={show ? 'Hide password' : 'Show password'}>
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

function SubmitButton({ busy, children, disabled }: { busy: boolean; children: ReactNode; disabled?: boolean }) {
  return (
    <button type="submit" className="btn primary lg auth-submit" disabled={busy || disabled}>
      {busy && <LoaderCircle size={15} className="spin" />}
      {children}
    </button>
  );
}

function ErrorLine({ error }: { error: string | null }) {
  return error ? <div className="auth-error">{error}</div> : null;
}

function useStatus() {
  const [status, setStatus] = useState<AuthStatus | null>(null);
  useEffect(() => {
    authApi.status().then(setStatus, () => setStatus({ userId: null, setupRequired: false, signupOpen: false, demo: null }));
  }, []);
  return status;
}

// ---------------------------------------------------------------------------

export function LoginPage() {
  const status = useStatus();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status?.userId) enterApp(nextPath());
    else if (status?.setupRequired) navigate('/setup', { replace: true });
  }, [status, navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await authApi.login(email, password);
      enterApp(nextPath());
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <AuthCard
      title="Welcome back"
      subtitle="Sign in to your Atrium workspace."
      footer={
        <>
          {status?.signupOpen && (
            <span>
              New here? <Link to="/setup">Create a workspace</Link>
            </span>
          )}
          {!status?.signupOpen && <span className="faint">Need access? Ask a teammate for an invite link.</span>}
        </>
      }
    >
      <form onSubmit={submit}>
        <Field label="Email">
          <input className="input" type="email" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
        </Field>
        <Field label="Password">
          <PasswordInput value={password} onChange={setPassword} placeholder="Your password" autoComplete="current-password" />
        </Field>
        <ErrorLine error={error} />
        <SubmitButton busy={busy} disabled={!email || !password}>
          Sign in
        </SubmitButton>
      </form>
      {status?.demo && (
        <div className="auth-demo">
          Local demo: sign in as <strong>rodilraemer@gmail.com</strong> (or maya@example.com) with password <code>{status.demo.password}</code>.
        </div>
      )}
    </AuthCard>
  );
}

export function SetupPage() {
  const status = useStatus();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status && !status.setupRequired && !status.signupOpen) navigate('/login', { replace: true });
  }, [status, navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await authApi.setup({ name, email, password, workspaceName });
      enterApp(`/${r.workspaceId}/home`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <AuthCard
      title={status?.setupRequired ? 'Set up Atrium' : 'Create a workspace'}
      subtitle={status?.setupRequired ? 'Create the owner account. You can invite your team right after.' : 'Create your account and a new workspace.'}
      footer={
        !status?.setupRequired && (
          <span>
            Already have an account? <Link to="/login">Sign in</Link>
          </span>
        )
      }
    >
      <form onSubmit={submit}>
        <Field label="Workspace name">
          <input className="input" autoFocus value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} placeholder="e.g. SeekLab" />
        </Field>
        <Field label="Your full name">
          <input className="input" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Cooper" />
        </Field>
        <Field label="Email">
          <input className="input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
        </Field>
        <Field label="Password">
          <PasswordInput value={password} onChange={setPassword} />
        </Field>
        <ErrorLine error={error} />
        <SubmitButton busy={busy} disabled={!name || !email || password.length < 8}>
          Create workspace
        </SubmitButton>
      </form>
    </AuthCard>
  );
}

export function InvitePage() {
  const { token = '' } = useParams();
  const status = useStatus();
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    authApi.getInvite(token).then(setInfo, (e) => setLoadError(errorMessage(e)));
  }, [token]);

  if (loadError) {
    return (
      <AuthCard title="Invite unavailable" subtitle={loadError} footer={<Link to="/login">Go to sign in</Link>}>
        {null}
      </AuthCard>
    );
  }
  if (!info || !status) {
    return (
      <div className="boot">
        <AtriumLogo className="boot-logo" />
      </div>
    );
  }

  const accept = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (info.hasAccount && !status.userId) await authApi.login(info.email, password);
      const r = await authApi.acceptInvite(token, info.hasAccount ? {} : { name, password });
      enterApp(`/${r.workspaceId}/home`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  const subtitle = (
    <>
      <strong>{info.inviterName}</strong> invited <strong>{info.email}</strong> to join <strong>{info.workspaceName}</strong>
      {info.role === 'guest' ? ' as a guest' : info.role === 'admin' ? ' as an admin' : ''}.
    </>
  );

  if (info.hasAccount) {
    return (
      <AuthCard title={`Join ${info.workspaceName}`} subtitle={subtitle}>
        <form onSubmit={accept}>
          {!status.userId && (
            <Field label={`Password for ${info.email}`}>
              <PasswordInput value={password} onChange={setPassword} autoFocus placeholder="Your password" autoComplete="current-password" />
            </Field>
          )}
          <ErrorLine error={error} />
          <SubmitButton busy={busy} disabled={!status.userId && !password}>
            {status.userId ? 'Accept invite' : 'Sign in & join'}
          </SubmitButton>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={`Join ${info.workspaceName}`} subtitle={subtitle}>
      {status.userId ? (
        <p className="auth-error">You're signed in with a different account. Sign out first, then open this link again.</p>
      ) : (
        <form onSubmit={accept}>
          <Field label="Email">
            <input className="input" value={info.email} disabled />
          </Field>
          <Field label="Your full name">
            <input className="input" autoFocus autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Cooper" />
          </Field>
          <Field label="Choose a password">
            <PasswordInput value={password} onChange={setPassword} />
          </Field>
          <ErrorLine error={error} />
          <SubmitButton busy={busy} disabled={!name || password.length < 8}>
            Create account & join
          </SubmitButton>
        </form>
      )}
    </AuthCard>
  );
}

export function ResetPage() {
  const { token = '' } = useParams();
  const [email, setEmail] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    authApi.getReset(token).then((r) => setEmail(r.email), (e) => setLoadError(errorMessage(e)));
  }, [token]);

  if (loadError) {
    return (
      <AuthCard title="Link unavailable" subtitle={loadError} footer={<Link to="/login">Go to sign in</Link>}>
        {null}
      </AuthCard>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) return setError('Passwords don’t match.');
    setBusy(true);
    setError(null);
    try {
      await authApi.resetPassword(token, password);
      enterApp('/');
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Set a new password" subtitle={email ? <>For {email}</> : undefined}>
      <form onSubmit={submit}>
        <Field label="New password">
          <PasswordInput value={password} onChange={setPassword} autoFocus />
        </Field>
        <Field label="Confirm password">
          <PasswordInput value={confirm} onChange={setConfirm} placeholder="Repeat the password" />
        </Field>
        <ErrorLine error={error} />
        <SubmitButton busy={busy} disabled={password.length < 8 || !confirm}>
          Save password & sign in
        </SubmitButton>
      </form>
    </AuthCard>
  );
}
